import { useCallback } from 'react';

import { markdownToHtml } from '../utils/converters';
import {
  autoLinkUrls,
  cleanHtml,
  containsMarkdown,
  extractFilesFromClipboard,
  hasRichFormatting,
  removeImagesFromHtml,
} from '../utils/pasteUtils';

interface UsePasteHandlerOptions {
  onPaste: (html: string) => void;
  onFilePaste?: (files: File[]) => Promise<string | null | undefined>;
}

const plainTextToHtml = (text: string): string => {
  const lines = text.split('\n');
  return lines
    .map((line) => `<p>${line || '<br>'}</p>`)
    .join('');
};

export const usePasteHandler = ({
  onPaste,
  onFilePaste,
}: UsePasteHandlerOptions) => {
  const handlePaste = useCallback(
    async(event: React.ClipboardEvent<HTMLDivElement>) => {
      const clipboardData = event.clipboardData;
      const html = clipboardData.getData('text/html');
      const text = clipboardData.getData('text/plain');

      // 1. File paste — upload only, attachment URL is shown separately
      const files = extractFilesFromClipboard(clipboardData);
      if (files.length > 0 && onFilePaste) {
        event.preventDefault();
        await onFilePaste(files);
        return;
      }

      // 2. Rich HTML — preserve formatting (Google Docs, web pages, etc.)
      if (html && hasRichFormatting(html)) {
        event.preventDefault();
        let processedHtml = cleanHtml(html);
        processedHtml = removeImagesFromHtml(processedHtml);
        processedHtml = autoLinkUrls(processedHtml);
        onPaste(processedHtml);
        return;
      }

      // Use plain text when HTML has no rich formatting
      if (!text) return;

      event.preventDefault();

      // 3. Markdown text — convert to HTML
      if (containsMarkdown(text)) {
        const convertedHtml = markdownToHtml(text);
        onPaste(autoLinkUrls(convertedHtml));
        return;
      }

      // 4. Plain text — each line becomes a paragraph
      onPaste(autoLinkUrls(plainTextToHtml(text)));
    },
    [onPaste, onFilePaste],
  );

  return { handlePaste };
};
